import React, { useEffect } from 'react';
import PageHeader from '../components/common/PageHeader';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  Skeleton,
} from '@mui/material';
import { Assessment, LocationOn, LocalShipping, Block } from '@mui/icons-material';
import StatCard from '../components/shared/StatCard';
import StatisticsChart, { MonthlyChartData } from '../components/statistics/StatisticsChart';
import { getCompanyStatistics, CompanyStatistics, getStatisticsOverview, exportStatisticsToExcel, exportStatisticsToPdf } from '../services/statisticsApi';
import { formatNumber, formatRejectRate } from '../utils/numberFormat';
import LicenseeFilter from '../components/common/LicenseeFilter';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { format } from 'date-fns';
import { buildDateRangeOptions, buildStatsDailyChartData } from './_shared/ghgStatsUtils';
import ExportButtonMenu from './_shared/ExportButtonMenu';
import PeriodFilterToggle from './_shared/PeriodFilterToggle';
import StatsChartSkeleton from './_shared/StatsChartSkeleton';
import StatCardsSkeleton from './_shared/StatCardsSkeleton';
import { MonthlySkeletonRow } from './_shared/RankingTableSkeleton';
import StatsDailyBreakdownTable from './_shared/StatsDailyBreakdownTable';
import { useCompanyPageState } from './_shared/useCompanyPageState';

// Merge one company's monthly rows into an accumulator keyed by year+month.
const aggregateMonthlyRows = (rows: any[]): any[] =>
  rows.reduce((acc: any[], curr) => {
    const existing = acc.find(m => m.year === curr.year && m.month === curr.month);
    if (existing) {
      existing.total_transactions = Number(existing.total_transactions) + Number(curr.total_transactions);
      existing.total_geolocations = Number(existing.total_geolocations) + Number(curr.total_geolocations);
      existing.total_unique_shipments = Number(existing.total_unique_shipments) + Number(curr.total_unique_shipments);
      existing.ytd_transactions = Math.max(Number(existing.ytd_transactions), Number(curr.ytd_transactions));
      existing.ytd_geolocations = Math.max(Number(existing.ytd_geolocations), Number(curr.ytd_geolocations));
      existing.ytd_unique_shipments = Math.max(Number(existing.ytd_unique_shipments), Number(curr.ytd_unique_shipments));
    } else {
      acc.push({
        ...curr,
        total_transactions: Number(curr.total_transactions),
        total_geolocations: Number(curr.total_geolocations),
        total_unique_shipments: Number(curr.total_unique_shipments),
        ytd_transactions: Number(curr.ytd_transactions),
        ytd_geolocations: Number(curr.ytd_geolocations),
        ytd_unique_shipments: Number(curr.ytd_unique_shipments)
      });
    }
    return acc;
  }, []);

// Transform a statistics overview into the CompanyStatistics shape (aggregated "All Companies").
const buildAggregatedStats = (overview: any, options: any): CompanyStatistics => ({
  company: {
    id: 0,
    name: 'All Companies',
    licence_type: 'Aggregated',
    shipment_limit: null,
    geo_limit: null,
    partner_name: null,
    is_demo: false,
    disabled: false,
    license_start: null,
    license_end: null,
    analytics_enabled: true
  },
  year: overview.year,
  fromDate: options.fromDate || null,
  toDate: options.toDate || null,
  availableYears: [],
  yearly: {
    total_transactions: Number(overview.aggregated.totalTransactions),
    total_geolocations: Number(overview.aggregated.totalGeolocations),
    total_unique_shipments: Number(overview.aggregated.totalUniqueShipments),
    total_errored_shipments: Number(overview.aggregated.totalErroredShipments)
  },
  monthly: aggregateMonthlyRows(
    overview.monthly.flatMap((m: any) =>
      m.companies.map((c: any) => ({
        year: m.year,
        month: m.month,
        month_name: m.month_name,
        total_transactions: Number(c.total_transactions),
        total_geolocations: Number(c.total_geolocations),
        total_unique_shipments: Number(c.total_unique_shipments),
        ytd_transactions: Number(c.ytd_transactions),
        ytd_geolocations: Number(c.ytd_geolocations),
        ytd_unique_shipments: Number(c.ytd_unique_shipments),
        transactions_left: c.transactions_left,
        geolocations_left: c.geolocations_left,
        percent_transactions_left: c.percent_transactions_left,
        percent_geolocations_left: c.percent_geolocations_left
      }))
    )
  ),
  daily: (overview.daily || []).map((d: any) => ({
    day: d.day,
    year: d.year,
    month: d.month,
    total_transactions: d.total_transactions,
    total_geolocations: d.total_geolocations,
    total_unique_shipments: d.total_unique_shipments,
  }))
});

// Add one day's totals into the per-day aggregate map (mutates map).
const addDayToAggregate = (map: Record<string, any>, day: any): void => {
  const dayKey = day.day;
  if (!map[dayKey]) {
    map[dayKey] = {
      day: day.day,
      year: day.year,
      month: day.month,
      total_transactions: 0,
      total_geolocations: 0,
      total_unique_shipments: 0
    };
  }
  map[dayKey].total_transactions = Number(map[dayKey].total_transactions) + Number(day.total_transactions || 0);
  map[dayKey].total_geolocations = Number(map[dayKey].total_geolocations) + Number(day.total_geolocations || 0);
  map[dayKey].total_unique_shipments = Number(map[dayKey].total_unique_shipments) + Number(day.total_unique_shipments || 0);
};

// Fetch & aggregate daily data across all companies for a date window.
const fetchAllCompaniesDaily = async (firstDay: string, lastDay: string): Promise<any[]> => {
  const overview = await getStatisticsOverview({ fromDate: firstDay, toDate: lastDay });
  const dailyAggregates: Record<string, any> = {};

  for (const company of overview.companies) {
    try {
      const companyStats = await getCompanyStatistics(company.company_id, { fromDate: firstDay, toDate: lastDay });
      companyStats.daily.forEach((day: any) => addDayToAggregate(dailyAggregates, day));
    } catch (err) {
      console.error(`Failed to fetch daily data for company ${company.company_id}:`, err);
    }
  }

  return Object.values(dailyAggregates).sort((a: any, b: any) =>
    new Date(a.day).getTime() - new Date(b.day).getTime()
  );
};

// Transform monthly data into sorted chart rows.
const buildMonthlyChartData = (monthly: any[] | undefined): MonthlyChartData[] => {
  if (!monthly) return [];
  return monthly.map((month) => ({
    month: month.month_name?.trim() || `Month ${month.month}`,
    monthNumber: month.month,
    year: month.year,
    transactions: Number(month.total_transactions) || 0,
    geolocations: Number(month.total_geolocations) || 0,
    uniqueShipments: Number(month.total_unique_shipments) || 0,
  })).sort((a, b) => (a.year === b.year ? a.monthNumber - b.monthNumber : a.year - b.year));
};

// Compute effective [start, end] for a month, clamped to a date-range filter.
const computeMonthRange = (
  effectiveYear: number,
  month: { month: number },
  filterMode: string,
  fromDate: Date | null,
  toDate: Date | null,
): { firstDay: string; lastDay: string } => {
  const monthStart = new Date(effectiveYear, month.month - 1, 1);
  const monthEnd = new Date(effectiveYear, month.month, 0);
  const effectiveStart =
    filterMode === 'dateRange' && fromDate && fromDate > monthStart ? fromDate : monthStart;
  const effectiveEnd =
    filterMode === 'dateRange' && toDate && toDate < monthEnd ? toDate : monthEnd;
  return { firstDay: format(effectiveStart, 'yyyy-MM-dd'), lastDay: format(effectiveEnd, 'yyyy-MM-dd') };
};

interface MonthlyBreakdownProps {
  loading: boolean;
  data: CompanyStatistics | null;
  selectedCompanyId: number | null;
  isMasterAdmin: boolean;
  selectedMonth: { year: number; month: number; month_name: string } | null;
  onMonthClick: (month: { year: number; month: number; month_name: string }) => void;
}

const MonthlyBreakdown: React.FC<MonthlyBreakdownProps> = ({
  loading, data, selectedCompanyId, isMasterAdmin, selectedMonth, onMonthClick,
}) => {
  const showCompanyCols = !loading && data && selectedCompanyId !== null;
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Monthly Breakdown
        {!loading && data && selectedCompanyId === null && isMasterAdmin && (
          <Typography variant="caption" color="text.secondary" display="block">
            Aggregated across all companies • Click a month to view daily breakdown
          </Typography>
        )}
        {showCompanyCols && (
          <Typography variant="caption" color="text.secondary" display="block">
            Click a month to view daily breakdown
          </Typography>
        )}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell align="right">Geolocations</TableCell>
              <TableCell align="right">Unique Shipments</TableCell>
              {showCompanyCols && (
                <>
                  <TableCell align="right">YTD Transactions</TableCell>
                  <TableCell align="right">YTD Geolocations</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || !data ? (
              ['ms0', 'ms1', 'ms2', 'ms3', 'ms4', 'ms5', 'ms6', 'ms7'].map((key, idx) => (
                <MonthlySkeletonRow key={key} idx={idx} />
              ))
            ) : data.monthly.map((month) => (
              <TableRow
                key={`${month.year}-${month.month}`}
                hover
                onClick={() => onMonthClick(month)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {month.month_name}
                    {selectedMonth?.year === month.year && selectedMonth?.month === month.month && ' ▼'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(month.total_transactions)}</TableCell>
                <TableCell align="right">{formatNumber(month.total_geolocations)}</TableCell>
                <TableCell align="right">{formatNumber(month.total_unique_shipments)}</TableCell>
                {selectedCompanyId !== null && (
                  <>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary">
                        {formatNumber(month.ytd_transactions)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary">
                        {formatNumber(month.ytd_geolocations)}
                      </Typography>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

interface DailyRangeLabelProps {
  filterMode: string;
  fromDate: Date | null;
  toDate: Date | null;
  selectedMonth: { year: number; month: number };
}

// The "(MMM dd – MMM dd, yyyy)" suffix shown only in dateRange mode.
const DailyRangeLabel: React.FC<DailyRangeLabelProps> = ({ filterMode, fromDate, toDate, selectedMonth }) => {
  if (!(filterMode === 'dateRange' && fromDate && toDate)) return null;
  const effectiveYear = selectedMonth.year;
  const monthStart = new Date(effectiveYear, selectedMonth.month - 1, 1);
  const monthEnd = new Date(effectiveYear, selectedMonth.month, 0);
  const effectiveStart = fromDate > monthStart ? fromDate : monthStart;
  const effectiveEnd = toDate < monthEnd ? toDate : monthEnd;
  return (
    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
      ({format(effectiveStart, 'MMM dd')} – {format(effectiveEnd, 'MMM dd, yyyy')})
    </Typography>
  );
};

// Company licence/status chips (or skeletons) for non-master view.
const CompanyStatusChips: React.FC<{ loading: boolean; data: CompanyStatistics | null }> = ({ loading, data }) => {
  if (loading || !data) {
    return (
      <Box display="flex" gap={1} alignItems="center" mb={2}>
        <Skeleton variant="text" width={220} sx={{ fontSize: '1.5rem' }} />
        <Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: '12px' }} />
        <Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: '12px' }} />
      </Box>
    );
  }
  return (
    <Box display="flex" gap={1} alignItems="center" mb={2}>
      <Chip label={data.company.licence_type} size="small" />
      {data.company.is_demo && <Chip label="Demo" size="small" color="warning" />}
      <Chip
        label={data.company.disabled ? 'Inactive' : 'Active'}
        size="small"
        color={data.company.disabled ? 'default' : 'success'}
      />
    </Box>
  );
};

const CompanyStatisticsPage: React.FC = () => {
  const { companyId: paramCompanyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    data, setData,
    loading, setLoading,
    error, setError,
    filterMode, setFilterMode,
    selectedYear, setSelectedYear,
    fromDate, setFromDate,
    toDate, setToDate,
    availableYears, setAvailableYears,
    exporting, setExporting,
    selectedCompanyId, setSelectedCompanyId,
    selectedMonth, setSelectedMonth,
    dailyData, setDailyData,
    loadingDaily, setLoadingDaily,
    dailyBreakdownRef,
  } = useCompanyPageState<CompanyStatistics>();

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
  }, []);

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin;

  // For Master Admin: use selectedCompanyId (null means "All Companies")
  // For Licensee Admin: use their company ID
  // From URL param: use that company ID
  const effectiveCompanyId = (() => {
    if (paramCompanyId) return Number.parseInt(paramCompanyId);
    if (isMasterAdmin) return selectedCompanyId;
    return user?.companyId;
  })();

  const getExportOptions = () => {
    const options: any = { ...buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear) };
    if (effectiveCompanyId) options.companyId = effectiveCompanyId;
    return options;
  };

  const handleExcelExport = async () => {
    setExporting(true);
    try {
      const downloadUrl = await exportStatisticsToExcel(getExportOptions());
      if (downloadUrl) {
        globalThis.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handlePdfExport = async () => {
    setExporting(true);
    try {
      const downloadUrl = await exportStatisticsToPdf(getExportOptions());
      if (downloadUrl) {
        globalThis.open(downloadUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to export to PDF');
    } finally {
      setExporting(false);
    }
  };


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, fromDate, toDate, filterMode, effectiveCompanyId]);

  const fetchData = async () => {
    if (!effectiveCompanyId && !isMasterAdmin) return;

    try {
      setLoading(true);
      setError(null);

      const options = buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear);

      if (effectiveCompanyId) {
        const result = await getCompanyStatistics(effectiveCompanyId, options);
        setData(result);
      } else if (isMasterAdmin) {
        // Show aggregated data for all companies
        const overview = await getStatisticsOverview({ ...options, includeDaily: true });
        setData(buildAggregatedStats(overview, options));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load company statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyData = async (month: { year: number; month: number; month_name: string }) => {
    setLoadingDaily(true);

    try {
      // In year mode, use selectedYear so the fetch stays in sync when the
      // year picker changes (month.year is frozen at click-time and goes stale).
      const effectiveYear = filterMode === 'year' ? selectedYear : month.year;

      const { firstDay, lastDay } = computeMonthRange(effectiveYear, month, filterMode, fromDate, toDate);

      if (selectedCompanyId === null) {
        setDailyData(await fetchAllCompaniesDaily(firstDay, lastDay));
      } else {
        const companyStats = await getCompanyStatistics(selectedCompanyId, {
          fromDate: firstDay,
          toDate: lastDay
        });

        const monthlyDaily = companyStats.daily.filter((day: any) =>
          day.year === effectiveYear && day.month === month.month
        );

        setDailyData(monthlyDaily);
      }
    } catch (err) {
      console.error('Failed to load daily data:', err);
      setDailyData([]);
    } finally {
      setLoadingDaily(false);
    }
  };

  // Re-fetch daily data whenever the date filter changes while a month is selected
  useEffect(() => {
    if (selectedMonth) {
      fetchDailyData(selectedMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode, fromDate, toDate, selectedCompanyId, selectedYear]);

  const handleMonthClick = async (month: { year: number; month: number; month_name: string }) => {
    // Toggle: if same month is clicked, collapse it
    if (selectedMonth?.year === month.year && selectedMonth?.month === month.month) {
      setSelectedMonth(null);
      setDailyData([]);
      return;
    }

    setSelectedMonth(month);
    await fetchDailyData(month);

    // Scroll to daily breakdown after data loads
    setTimeout(() => {
      dailyBreakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  if (!loading && error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!loading && !data) return null;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        description={data?.company?.name && data.company.name !== 'All Companies' ? `Statistics report for ${data.company.name}.` : 'Statistics scoped to your licensee.'}
        actions={
          <ExportButtonMenu
            onRefresh={fetchData}
            onExcelExport={handleExcelExport}
            onPdfExport={handlePdfExport}
            exporting={exporting}
          />
        }
      />
      {!isMasterAdmin && <CompanyStatusChips loading={loading} data={data} />}

      {/* Filter Bar */}
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {isMasterAdmin && (
          <LicenseeFilter
            value={paramCompanyId ? Number.parseInt(paramCompanyId) : (selectedCompanyId || '')}
            onChange={(id) => {
              const newId = id === '' ? null : (id);
              if (paramCompanyId) {
                navigate(newId ? `/statistics/company/${newId}` : '/statistics/company');
              } else {
                setSelectedCompanyId(newId);
              }
            }}
            label="Select Licensee"
            sx={{ minWidth: 300 }}
          />
        )}
        <PeriodFilterToggle
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          years={availableYears}
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
          yearSelectorSx={{ mb: '0' }}
        />
      </Paper>

      {/* Summary Cards */}
      {loading || !data ? (
        <StatCardsSkeleton count={4} mdCols={3} lines={3} />
      ) : (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Transactions"
              value={data.yearly.total_transactions}
              subtitle="This year"
              icon={<Assessment />}
              color="#1976d2"
              progress={data.company.shipment_limit ? {
                value: data.yearly.total_transactions,
                max: data.company.shipment_limit
              } : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Geolocations"
              value={data.yearly.total_geolocations}
              subtitle="This year"
              icon={<LocationOn />}
              color="#2e7d32"
              progress={data.company.geo_limit ? {
                value: data.yearly.total_geolocations,
                max: data.company.geo_limit
              } : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unique Shipments"
              value={data.yearly.total_unique_shipments}
              subtitle="This year"
              icon={<LocalShipping />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="% of Rejects"
              value={formatRejectRate(data.yearly.total_errored_shipments, data.yearly.total_transactions)}
              icon={<Block />}
              color="#d32f2f"
            />
          </Grid>
        </Grid>
      )}

      {/* Statistics Trend Chart */}
      {loading || !data ? (
        <StatsChartSkeleton />
      ) : (
        <StatisticsChart
          data={buildMonthlyChartData(data?.monthly)}
          dailyData={buildStatsDailyChartData(data?.daily)}
          title="Statistics Trend"
          height={350}
        />
      )}

      {/* Monthly Breakdown */}
      <MonthlyBreakdown
        loading={loading}
        data={data}
        selectedCompanyId={selectedCompanyId}
        isMasterAdmin={isMasterAdmin}
        selectedMonth={selectedMonth}
        onMonthClick={handleMonthClick}
      />

      {/* Daily Breakdown for Selected Month */}
      {selectedMonth && (
        <Paper ref={dailyBreakdownRef} sx={{ p: 3, mb: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Daily Breakdown - {selectedMonth.month_name}
            <DailyRangeLabel
              filterMode={filterMode}
              fromDate={fromDate}
              toDate={toDate}
              selectedMonth={selectedMonth}
            />
          </Typography>
          <StatsDailyBreakdownTable loading={loadingDaily} data={dailyData} />
        </Paper>
      )}
    </Box>
  );
};

export default CompanyStatisticsPage;
